import React from "react";
import { useQuery } from "@tanstack/react-query";
import { MousePointerClick, ArrowLeftRight, DollarSign, TrendingUp } from "lucide-react";
import { api } from "@/lib/api";
import { StatCard } from "@/components/Card";
import { formatCurrency, formatNumber, formatPercent } from "@/lib/utils";

export function DashboardPage() {
  const today = new Date().toISOString().split("T")[0];
  const tomorrow = new Date(Date.now() + 86400000).toISOString().split("T")[0];

  const { data: summary, isLoading } = useQuery({
    queryKey: ["dashboard", "summary", today],
    queryFn: async () => {
      const res = await api.get("/v1/reporting/dashboard", { from: today, to: tomorrow });
      return res.json();
    },
  });

  const stats = summary?.data || {
    clicks: 0,
    uniqueClicks: 0,
    conversions: 0,
    revenue: 0,
    payout: 0,
    profit: 0,
    cr: 0,
    epc: 0,
  };

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
        <p className="text-gray-500 mt-1">Today's performance overview</p>
      </div>

      {isLoading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={i} className="rounded-xl border border-gray-200 bg-white shadow-sm p-6 animate-pulse">
              <div className="h-4 bg-gray-200 rounded w-24 mb-3" />
              <div className="h-8 bg-gray-200 rounded w-32" />
            </div>
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard
            title="Total Clicks"
            value={formatNumber(stats.clicks)}
            icon={<MousePointerClick className="w-5 h-5" />}
          />
          <StatCard
            title="Unique Clicks"
            value={formatNumber(stats.uniqueClicks)}
            icon={<MousePointerClick className="w-5 h-5" />}
          />
          <StatCard
            title="Conversions"
            value={formatNumber(stats.conversions)}
            icon={<ArrowLeftRight className="w-5 h-5" />}
          />
          <StatCard
            title="Conv. Rate"
            value={formatPercent(stats.cr)}
            icon={<TrendingUp className="w-5 h-5" />}
          />
          <StatCard
            title="Revenue"
            value={formatCurrency(stats.revenue)}
            icon={<DollarSign className="w-5 h-5" />}
          />
          <StatCard
            title="Payout"
            value={formatCurrency(stats.payout)}
            icon={<DollarSign className="w-5 h-5" />}
          />
          <StatCard
            title="Profit"
            value={formatCurrency(stats.profit)}
            changeType={stats.profit >= 0 ? "positive" : "negative"}
            icon={<DollarSign className="w-5 h-5" />}
          />
          <StatCard
            title="EPC"
            value={formatCurrency(stats.epc)}
            icon={<TrendingUp className="w-5 h-5" />}
          />
        </div>
      )}
    </div>
  );
}
